// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file useLayer.tsx
 * @input Uses React hooks, Popover API, CSS anchor positioning, typography tokens
 * @output Exports useLayer hook for layer positioning and visibility
 * @position Core layer utility; used by useHoverCard, useTooltip, etc.
 *
 * SYNC: When modified, update:
 * - /packages/core/src/Layer/useLayer.doc.mjs
 * - /packages/core/src/Layer/useLayer.test.tsx
 * - /packages/core/src/Layer/index.ts
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { addAnchorName, removeAnchorName } from "./anchorName.js";
import "../theme/tokens.stylex.js";
import { typographyVars } from "../theme/tokens.stylex.js";
import { jsx as _jsx } from "react/jsx-runtime";
const styles = {
  base: {
    keoZOQ: "xdj266r",
    k1K539: "xat24cr",
    keTefX: "x1lziwak",
    k71WvV: "x14z9mp",
    kLKAdn: "xexx8yu",
    kGO01o: "x18d9i69",
    kZCmMZ: "x1c1uobl",
    kwRFfy: "xyri2b",
    kMzoRj: "xc342km",
    ksu8eU: "xng3xce",
    kVQacm: "x1rea2x4",
    kMv6JI: "x9ynric",
    kWkggS: "xjbqb8w",
    $$css: true
  },
  fixed: {
    kVAEAm: "xixxii4",
    $$css: true
  }
};

/**
 * Position placement relative to anchor.
 * Logical: start/end resolve against the popover's own inherited direction
 * via CSS (RTL contexts mirror automatically, no JS involved).
 */

/**
 * Alignment along the placement axis
 */

/**
 * Render props for context mode (anchor positioning)
 */

/**
 * Render props for fixed mode (manual coordinates)
 */

/**
 * Base options shared by both modes
 */

/**
 * Options for context mode (CSS anchor positioning)
 */

/**
 * Options for fixed mode (manual positioning)
 */

/**
 * Return type for context mode
 */

/**
 * Return type for fixed mode
 */

/**
 * Map logical placement/alignment to a CSS position-area value.
 *
 * Uses the self-* logical keyword family: the inline axis resolves against
 * the popover's own inherited direction (the layer renders inside the
 * trigger's subtree, so it inherits `direction` and mirrors in RTL with no
 * JS). The block axis is direction-neutral but must come from the same
 * keyword family — mixing physical `top` with `self-inline-*` produces an
 * invalid position-area (computes to `none`, which pins the popover to the
 * viewport corner because styles.base zeroes the UA margins).
 *
 * Note the plain logical family (`inline-start`, no `self-`) is NOT a
 * substitute: it resolves against the containing block — the page root for
 * a top-layer popover — so it ignores `direction` set on a subtree, which
 * is exactly #3389's repro.
 */
function getPositionArea(placement = 'above', alignment = 'center') {
  if (placement === 'above' || placement === 'below') {
    const block = placement === 'above' ? 'self-block-start' : 'self-block-end';
    if (alignment === 'start') {
      return `${block} span-self-inline-end`;
    }
    if (alignment === 'end') {
      return `${block} span-self-inline-start`;
    }
    return block; // center
  }
  const inline = placement === 'start' ? 'self-inline-start' : 'self-inline-end';
  if (alignment === 'start') {
    return `${inline} span-self-block-end`;
  }
  if (alignment === 'end') {
    return `${inline} span-self-block-start`;
  }
  return inline; // center
}

/**
 * Compute the `position-try-fallbacks` list for a placement/alignment pair.
 *
 * Flips alone cannot rescue a centered layer — flipping along the alignment
 * axis maps center → center, so overflow on that axis renders clipped
 * (#3671). Centered alignments therefore append span-based fallbacks letting
 * the browser slide the layer along the alignment axis as a last resort
 * (same-side spans first). Flips already resolve non-centered alignments.
 */
export function getPositionTryFallbacks(placement = 'above', alignment = 'center') {
  const flips = 'flip-block, flip-inline, flip-block flip-inline';
  if (alignment !== 'center') {
    return flips;
  }
  if (placement === 'above' || placement === 'below') {
    const [same, opposite] = placement === 'above' ? ['top', 'bottom'] : ['bottom', 'top'];
    return `${flips}, ${same} span-left, ${same} span-right, ${opposite} span-left, ${opposite} span-right`;
  }
  const [same, opposite] = placement === 'start' ? ['left', 'right'] : ['right', 'left'];
  return `${flips}, ${same} span-top, ${same} span-bottom, ${opposite} span-top, ${opposite} span-bottom`;
}

/**
 * Core layer hook that handles popover behavior and positioning.
 *
 * Supports two positioning modes with type-safe render props:
 * - `context`: CSS anchor positioning relative to a trigger element
 * - `fixed`: Fixed positioning at specified coordinates
 *
 * @example
 * ```
 * const layer = useLayer({ mode: 'context' });
 * <button ref={layer.ref}>Trigger</button>
 * {layer.render(<Content />, { placement: 'above', alignment: 'center' })}
 * ```
 */

export function useLayer(options) {
  const {
    mode,
    onShow,
    onHide,
    lightDismiss = false
  } = options;
  const id = useId();
  const anchorId = `--astryx-layer-${id.replace(/:/g, '')}`;
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef(null);
  const triggerRef = useRef(null);

  // Ref mirrors isOpen for synchronous reads inside show/hide.
  // State drives re-renders; the ref lets the imperative calls avoid
  // stale-closure reads of the previous isOpen value.
  const isOpenRef = useRef(false);
  const show = useCallback(() => {
    const popover = popoverRef.current;
    if (popover && !isOpenRef.current) {
      // Finding infra-4: the Popover API is unsupported on Safari <17 and
      // Firefox <125. On those browsers `showPopover` does not exist, so
      // calling it unconditionally throws a TypeError and the layer never
      // opens. Guard behind a feature check; when the API is missing, fall
      // back to plain visibility (the [popover] attribute is inert there, so
      // the element sits in normal flow) so the layer still becomes visible.
      if (typeof popover.showPopover === 'function') {
        popover.showPopover();
      } else {
        popover.style.display = 'block';
      }
      isOpenRef.current = true;
      setIsOpen(true);
      onShow?.();
    }
  }, [onShow]);
  const hide = useCallback(() => {
    if (isOpenRef.current) {
      const el = popoverRef.current;
      // See finding infra-4 note in `show`: mirror the same guard on hide so
      // unsupported browsers degrade gracefully instead of throwing.
      if (el) {
        if (typeof el.hidePopover === 'function') {
          el.hidePopover();
        } else {
          el.style.display = 'none';
        }
      }
      isOpenRef.current = false;
      setIsOpen(false);
      onHide?.();
    }
  }, [onHide]);

  // Ref for trigger element (context mode only)
  const ref = mode === 'context' ? el => {
    // Remove only THIS layer's anchor name from the previous element so
    // other layers sharing the same trigger keep their anchors.
    if (triggerRef.current && triggerRef.current !== el) {
      removeAnchorName(triggerRef.current, anchorId);
    }
    if (el) {
      addAnchorName(el, anchorId);
    }
    triggerRef.current = el;
  } : undefined;

  // Reconcile browser-initiated closes (light-dismiss, popover="auto" stack
  // eviction). These are the only cases where the DOM mutates without going
  // through our show/hide — we sync React state back to match.
  //
  // No "open" case: the browser never spontaneously opens a popover. Opens
  // only happen via showPopover() which we always call from show().
  //
  // The isOpenRef guard prevents double-firing: when our hide() already set
  // the ref to false, the subsequent toggle event (which the browser fires
  // as a side-effect of hidePopover) sees false and skips.
  const handleToggle = useCallback(e => {
    const toggleEvent = e;
    if (toggleEvent.newState === 'closed' && isOpenRef.current) {
      isOpenRef.current = false;
      setIsOpen(false);
      onHide?.();
    }
  }, [onHide]);

  // Ref callback for popover element — sets up the `toggle` listener.
  // Tracks the element + handler currently bound so the listener is removed
  // when the element detaches or when `handleToggle`'s identity changes (a new
  // `onHide` prop), preventing stale-closure listeners from accumulating on the
  // same element (infra-10).
  const listenedElRef = useRef(null);
  const listenedHandlerRef = useRef(null);
  const bindToggleListener = useCallback((el, handler) => {
    if (listenedElRef.current && listenedHandlerRef.current && (listenedElRef.current !== el || listenedHandlerRef.current !== handler)) {
      listenedElRef.current.removeEventListener('toggle', listenedHandlerRef.current);
      listenedElRef.current = null;
      listenedHandlerRef.current = null;
    }
    if (el && listenedElRef.current !== el) {
      el.addEventListener('toggle', handler);
      listenedElRef.current = el;
      listenedHandlerRef.current = handler;
    }
  }, []);
  const popoverRefCallback = useCallback(el => {
    popoverRef.current = el;
    bindToggleListener(el, handleToggle);
  }, [handleToggle, bindToggleListener]);

  // Re-bind when the handler identity changes while the element stays mounted,
  // and detach on unmount.
  useEffect(() => {
    if (popoverRef.current) {
      bindToggleListener(popoverRef.current, handleToggle);
    }
    return () => {
      if (listenedElRef.current && listenedHandlerRef.current) {
        listenedElRef.current.removeEventListener('toggle', listenedHandlerRef.current);
        listenedElRef.current = null;
        listenedHandlerRef.current = null;
      }
    };
  }, [handleToggle, bindToggleListener]);

  // Render function for context mode
  const renderContext = useCallback((children, props) => {
    const {
      placement = 'above',
      alignment = 'center',
      positioning = 'anchor',
      role,
      xstyle,
      className: extraClassName,
      style: extraStyle,
      as: Container = 'div',
      onMouseEnter,
      onMouseLeave
    } = props || {};

    // CSS anchor positioning (dynamic, not in StyleX)
    const anchorStyle = positioning === 'custom' ?
    // Consumer authors its own position styles via `style` — keep
    // only the anchor wiring, derive nothing from placement.
    {
      positionAnchor: anchorId
    } : {
      positionAnchor: anchorId,
      positionArea: getPositionArea(placement, alignment),
      positionTryFallbacks: getPositionTryFallbacks(placement, alignment)
    };
    const stylexResult = stylex.props(styles.base, xstyle);
    const combinedClassName = extraClassName ? `${extraClassName} ${stylexResult.className ?? ''}` : stylexResult.className;

    // Render as the requested tag. A `span` keeps the layer phrasing content
    // so it is valid (and stays put on hydration) inside inline contexts like
    // a `<p>`; `div` remains the default for block layers.
    return /*#__PURE__*/_jsx(Container, {
      ref: popoverRefCallback,
      id: id,
      role: role,
      popover: lightDismiss ? 'auto' : 'manual',
      className: combinedClassName,
      style: {
        ...stylexResult.style,
        ...anchorStyle,
        ...extraStyle
      },
      onMouseEnter: onMouseEnter,
      onMouseLeave: onMouseLeave,
      children: children
    });
  }, [anchorId, id, lightDismiss, popoverRefCallback]);

  // Render function for fixed mode
  const renderFixed = useCallback((children, props) => {
    const {
      x,
      y,
      xstyle,
      className: extraClassName,
      style: extraStyle
    } = props;

    // Dynamic position values
    const positionStyle = {
      top: y,
      left: x
    };
    const stylexResult = stylex.props(styles.base, styles.fixed, xstyle);
    const combinedClassName = extraClassName ? `${extraClassName} ${stylexResult.className ?? ''}` : stylexResult.className;
    return /*#__PURE__*/_jsx("div", {
      ref: popoverRefCallback,
      id: id,
      popover: lightDismiss ? 'auto' : 'manual',
      className: combinedClassName,
      style: {
        ...stylexResult.style,
        ...positionStyle,
        ...extraStyle
      },
      children: children
    });
  }, [popoverRefCallback, id, lightDismiss]);
  if (mode === 'context') {
    return {
      ref: ref,
      anchorId,
      show,
      hide,
      isOpen,
      id,
      render: renderContext
    };
  }
  return {
    ref: undefined,
    show,
    hide,
    isOpen,
    id,
    render: renderFixed
  };
}